import { useParams, Link } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { movies } from '@/mocks/movies';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const movie = movies.find(m => m.id === id);

  if (!movie) {
    return (
      <div className="min-h-screen bg-background-50 pt-16 flex items-center justify-center">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
            <i className="ri-emotion-sad-line text-5xl text-foreground-300"></i>
          </div>
          <h2 className="text-xl font-semibold text-foreground-800 mb-2">影片未找到</h2>
          <p className="text-foreground-500 text-sm mb-6">该影片可能已下架或不存在</p>
          <Link
            to="/"
            className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          >
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  const relatedMovies = movies
    .filter(m => m.id !== movie.id && m.category === movie.category)
    .slice(0, 6);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": `${movie.title} - 八戒影院免费在线观看`,
    "description": movie.description,
    "author": {
      "@type": "Organization",
      "name": "八戒影院"
    },
    "publisher": {
      "@type": "Organization",
      "name": "八戒影院",
      "url": "https://bajiecinema.com/"
    },
    "datePublished": `${movie.year}-01-01`,
    "image": movie.imageUrl
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "首页",
        "item": "https://bajiecinema.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": movie.category === 'tvseries' ? '电视剧' : '电影',
        "item": `https://bajiecinema.com/category/${movie.category}`
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": movie.title
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background-50 pt-16">
      <SchemaOrg data={articleSchema} />
      <SchemaOrg data={breadcrumbSchema} />
      <div className="w-full px-4 md:px-6 py-6">
        <div className="max-w-[1200px] mx-auto">
          <Breadcrumb
            items={[
              { label: movie.category === 'tvseries' ? '电视剧' : '电影', href: `/category/${movie.category}` },
              { label: movie.title },
            ]}
          />

          <article>
            <div className="flex flex-col lg:flex-row gap-8 mb-12">
              <div className="lg:w-[320px] flex-shrink-0">
                <div className="rounded-xl overflow-hidden bg-background-200 aspect-[2/3] lg:sticky lg:top-24">
                  <img
                    src={movie.imageUrl}
                    alt={`${movie.title} - 八戒影院免费在线观看`}
                    title={`${movie.title} - 高清免费在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  {movie.title}
                </h1>
                <p className="text-sm text-foreground-500 mb-6">
                  {movie.description}
                </p>

                <div className="flex items-center gap-3 mb-6 flex-wrap">
                  <div className="flex items-center gap-1.5 bg-accent-100 text-accent-800 px-3 py-1.5 rounded-lg">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-star-fill text-sm"></i>
                    </div>
                    <span className="text-sm font-semibold">{movie.rating}</span>
                  </div>
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                    {movie.year}
                  </span>
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                    {movie.region}
                  </span>
                  <span className="text-xs px-3 py-1.5 rounded-lg bg-secondary-100 text-secondary-700 font-medium whitespace-nowrap">
                    {movie.duration}
                  </span>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  <div className="bg-background-100 rounded-lg p-4">
                    <h3 className="text-xs text-foreground-500 mb-1">导演</h3>
                    <p className="text-sm font-medium text-foreground-800">{movie.director}</p>
                  </div>
                  <div className="bg-background-100 rounded-lg p-4">
                    <h3 className="text-xs text-foreground-500 mb-1">主演</h3>
                    <p className="text-sm font-medium text-foreground-800">{movie.cast.join('、')}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2 flex-wrap mb-8">
                  {movie.genre.map((g, i) => (
                    <span key={i} className="text-xs px-3 py-1.5 rounded-full bg-primary-100 text-primary-700 font-medium whitespace-nowrap">
                      {g}
                    </span>
                  ))}
                </div>

                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full font-medium text-sm hover:bg-primary-600 transition-all cursor-pointer whitespace-nowrap"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-play-circle-fill text-lg"></i>
                  </div>
                  立即观看
                </a>
              </div>
            </div>

            <div className="mb-12">
              <h2 className="text-lg font-semibold text-foreground-900 mb-4">剧情简介</h2>
              <div className="bg-background-100 rounded-lg p-5 md:p-6">
                <p className="text-sm text-foreground-700 leading-relaxed">
                  {movie.synopsis}
                </p>
              </div>
            </div>
          </article>

          {relatedMovies.length > 0 && (
            <section className="mb-8">
              <div className="flex items-center justify-between mb-5">
                <h2 className="text-lg font-semibold text-foreground-900">相关推荐</h2>
                <Link
                  to={`/category/${movie.category}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                  查看更多
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5" data-product-shop="">
                {relatedMovies.map((m) => (
                  <MovieCard key={m.id} movie={m} />
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}